import {
  Button,
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  Icons,
  Image,
  List,
  notification,
  ShowButton,
  Space,
  Table,
  TableProps,
} from '@pankod/refine-antd';
import { CanAccess, IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { SearchNew } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { ISupplier, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import { getAllSupplier, getSuppliersExport } from 'services/supplier';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import { getAddressAsString } from 'utilities/shared';

export const SupplierList: React.FC<IResourceComponentsProps> = () => {
  const [suppliers, setSuppliers] = useState<ISupplier[]>([]);
  const [totalSuppliers, setTotalSuppliers] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);

  const { push } = useNavigation();

  const fetchDataOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllSupplier({
        searchString: searchText,
        start: page,
        limit,
      });
      const suppliersData = data.data;
      setSuppliers(suppliersData);
      setTotalSuppliers(data.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [limit, page, searchText]);

  useEffect(() => {
    fetchDataOnMount();
  }, [fetchDataOnMount]);

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onDeleteSuccessHandler = async () => {
    try {
      const data = await getAllSupplier();
      const suppliersData = data.data;
      setSuppliers(suppliersData);
      setTotalSuppliers(data.total);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const suppliersData = await getSuppliersExport();
      const rows = [['Code', 'Company Name', 'Email', 'Phone No', 'Address']];
      suppliersData.forEach((element) => {
        rows.push([
          element.supplierCode,
          element.name,
          element.email,
          element.mobileNumber,
          JSON.stringify(getAddressAsString(element.address)),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeader = (
    <Space wrap size={[8, 8]} style={{ width: '100%' }}>
      <CreateButton type="primary" />
      <Button
        size="middle"
        icon={<Icons.ImportOutlined />}
        onClick={() => push('/suppliers/import')}
      >
        Import
      </Button>
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick}></ExportButton>
      <CanAccess resource="activity-history" action="list">
        <Button
          icon={<Icons.HistoryOutlined />}
          onClick={() => push(getActivityHistoryUrl('supplier'))}
        >
          Activity
        </Button>
      </CanAccess>
    </Space>
  );

  const onTableChange: TableProps<ISupplier>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra,
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: pageHeader }}>
      <MetaData />
      <SearchNew
        placeholder="Search by Supplier Code, Name, Email, Mobile Number, Tags"
        size="large"
        className="search"
        index={SearchIndex.Supplier}
        groups={['supplierCode', 'name', 'email', 'mobileNumber', 'tag']}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMount}
      />
      <CustomTableWithPagination
        total={totalSuppliers}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={suppliers}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalSuppliers,
          //   current: page,
          // }}
          pagination={false}
        >
          <Table.Column<ISupplier>
            dataIndex="avatar"
            title="Logo"
            width="6%"
            render={(_text, record): React.ReactNode => {
              const supplierPhoto = record?.supplierPhoto;
              const avatarURL = supplierPhoto[supplierPhoto?.length - 1]?.avatar?.thumbUrl;

              return (
                <Image
                  src={avatarURL || defaultAvatar}
                  style={{ objectFit: 'cover' }}
                  height={45}
                  width={45}
                  alt={record.name}
                />
              );
            }}
          />
          <Table.Column<ISupplier> dataIndex="supplierCode" title="Code" />
          <Table.Column<ISupplier> dataIndex="name" title="Company Name" />
          <Table.Column<ISupplier> dataIndex="email" title="Email" />
          <Table.Column<ISupplier> dataIndex="mobileNumber" title="Phone" />
          <Table.Column<ISupplier>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_text, record): React.ReactNode => {
              return (
                <Space style={{ padding: '1rem' }}>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <EditButton size="small" recordItemId={record.id} hideText />
                  <DeleteButton
                    size="small"
                    hideText
                    recordItemId={record.id}
                    onSuccess={onDeleteSuccessHandler}
                    // confirmTitle="Are you sure you want to delete this supplier?"
                  />
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};
